import AllotmentsEditorHeader from './AllotmentsEditorHeader';
import { KoobInputCheckbox, KoobTooltip } from '@koob/ui';
import AllotmentsEditorRoom from './AllotmentsEditorRoom';
import KoobPlaceholder from '../../../components/Koob/KoobPlaceholder';
import { useField } from 'formik';
import AllotmentsEditorEditButton from './AllotmentsEditorEditButton';
import { useApp } from '../../../hooks';
import { useState } from 'react';
import { decodeId } from '../../../utils';
import AllotmentsEditorOrganizationSelector from './AllotmentsEditorOrganizationSelector';
import { useTranslation } from 'react-i18next';
import { dayjs, toDateString } from '@koob/dates';

export default function AllotmentsEditorBody({
  initialValues,
  loading,
  currentMonth,
  daysInMonth,
  selectedDays,
  setSelectedDays,
  reload,
}) {
  const { t } = useTranslation('contracts');
  const { currentOrganizationId } = useApp();
  const [{ value: rooms }] = useField('rooms');

  const currentOrganizationIdDecoded = decodeId(currentOrganizationId);
  const [organizationId, setOrganizationId] = useState(
    currentOrganizationIdDecoded,
  );

  const canShowSharedAllotments =
    organizationId === currentOrganizationIdDecoded;
  const [showSharedAllotments, setShowSharedAllotments] = useState(true);

  const isDaySelected = (date, room) => {
    return selectedDays.some(
      (d) => d.roomId === room.id && d.day === toDateString(date),
    );
  };

  const toggleSelectDay = (date, room) => {
    const formattedDate = toDateString(date);

    if (isDaySelected(date, room)) {
      setSelectedDays(
        selectedDays.filter(
          (d) => d.day !== formattedDate || d.roomId !== room.id,
        ),
      );
    } else {
      setSelectedDays([
        ...selectedDays,
        { day: formattedDate, roomId: room.id },
      ]);
    }
  };

  // Same rule as a single click: only future days the displayed organization
  // is sold on (i.e. has a dedicated allotment day) can be selected.
  const selectableDaysOfRoom = (room) => {
    const organization = room.organizations?.find(
      (org) => parseInt(org.id) === organizationId,
    );

    return [...Array(daysInMonth)]
      .map((_, i) => ({
        day: toDateString(
          dayjs()
            .month(currentMonth)
            .date(i + 1),
        ),
        roomId: room.id,
      }))
      .filter(
        (d) =>
          !dayjs(d.day).isBefore(dayjs(), 'day') &&
          organization?.allotmentDays?.some(
            (ad) => ad.day === d.day && !ad.shared,
          ),
      );
  };

  const selectAll = () => {
    setSelectedDays(rooms.flatMap((room) => selectableDaysOfRoom(room)));
  };

  const deselectAll = () => {
    setSelectedDays([]);
  };

  const selectAllFromRoom = (room) => {
    setSelectedDays([
      ...selectedDays.filter((d) => d.roomId !== room.id),
      ...selectableDaysOfRoom(room),
    ]);
  };

  const deselectAllFromRoom = (room) => {
    setSelectedDays(selectedDays.filter((d) => d.roomId !== room.id));
  };

  const updatedRooms = initialValues?.rooms?.map((item) => {
    const findRoom = (rooms ?? []).find((room) => room.id === item.id);
    return {
      ...item,
      organizations: findRoom ? findRoom?.organizations : item?.organizations,
    };
  });

  return (
    <div className="my-3">
      <div className="bg-gray-50 border-t border-x border-gray-200 rounded-t-md">
        <div className="p-3 w-full flex justify-between items-center">
          <AllotmentsEditorOrganizationSelector
            organizationId={organizationId}
            setOrganizationId={setOrganizationId}
          />

          <div>
            <KoobTooltip
              tip={t('showSharedTooltip')}
              disabled={!canShowSharedAllotments}
            >
              <KoobInputCheckbox
                label={t('showShared')}
                id="showShared"
                checked={showSharedAllotments}
                onCheckedChange={(checked) => setShowSharedAllotments(checked)}
                disabled={!canShowSharedAllotments}
              />
            </KoobTooltip>
          </div>

          <AllotmentsEditorEditButton
            selectedOrganizationId={organizationId}
            selectedDays={selectedDays}
            rooms={updatedRooms}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md rounded-t-none border border-gray-200 overflow-hidden">
        <div className="min-h-[25vh] inline-block min-w-full">
          <div className="min-w-full divide-y divide-gray-200">
            <AllotmentsEditorHeader
              currentMonth={currentMonth}
              daysInMonth={daysInMonth}
              selectAll={() => selectAll()}
              deselectAll={() => deselectAll()}
            />

            {!loading ? (
              updatedRooms?.map((room) => (
                <AllotmentsEditorRoom
                  key={room.id}
                  organizationId={organizationId}
                  room={room}
                  currentMonth={currentMonth}
                  daysInMonth={daysInMonth}
                  toggleSelect={toggleSelectDay}
                  selectAll={() => selectAllFromRoom(room)}
                  deselectAll={() => deselectAllFromRoom(room)}
                  isSelected={isDaySelected}
                  showSharedAllotments={
                    canShowSharedAllotments && showSharedAllotments
                  }
                  reload={reload}
                />
              ))
            ) : (
              <KoobPlaceholder className="w-full h-16" count={3} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
